import { useEffect, useMemo } from "react";

import { getOfficeVesselOptions } from "../lib/officeVesselScope";
import { normalizeVesselId } from "../lib/vesselIds";
import { useOfficeVesselScopeStore } from "../stores/officeVesselScopeStore";
import type { AuthVesselOption } from "../types/auth";
import { useAuth } from "./useAuth";

interface OfficeVesselScopeResult {
  isEnabled: boolean;
  vesselOptions: AuthVesselOption[];
  selectedVesselId: AuthVesselOption["id"] | null;
  selectedVessel: AuthVesselOption | null;
  setSelectedVesselId: (vesselId: AuthVesselOption["id"] | null) => void;
}

export function useOfficeVesselScope(): OfficeVesselScopeResult {
  const { user } = useAuth();
  const storedSelectedVesselId = useOfficeVesselScopeStore((state) => state.selectedVesselId);
  const setSelectedVesselId = useOfficeVesselScopeStore((state) => state.setSelectedVesselId);
  const clearSelectedVesselId = useOfficeVesselScopeStore((state) => state.clearSelectedVesselId);

  const vesselOptions = useMemo(() => getOfficeVesselOptions(user), [user]);
  const normalizedStoredSelectedVesselId = normalizeVesselId(storedSelectedVesselId);

  const selectedVessel = useMemo(() => {
    if (vesselOptions.length === 0) {
      return null;
    }

    return vesselOptions.find((option) => option.id === normalizedStoredSelectedVesselId) ?? null;
  }, [normalizedStoredSelectedVesselId, vesselOptions]);

  useEffect(() => {
    if (user?.type !== "office") {
      clearSelectedVesselId();
      return;
    }
    if (selectedVessel === null) {
      if (normalizedStoredSelectedVesselId !== null) {
        clearSelectedVesselId();
      }
    }
  }, [
    clearSelectedVesselId,
    normalizedStoredSelectedVesselId,
    selectedVessel,
    user?.type,
    vesselOptions.length,
  ]);

  return {
    isEnabled: user?.type === "office" && vesselOptions.length > 0,
    vesselOptions,
    selectedVesselId: selectedVessel?.id ?? null,
    selectedVessel,
    setSelectedVesselId,
  };
}
